import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  Pagination,
  Fade,
  Tooltip
} from '@mui/material';
import {
  Search,
  Copy,
  Trash2,
  Pencil,
  SearchX,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  Link2Off
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StyledButton = styled(Button)(({ theme }) => ({
  background: '#7C3AED',
  color: '#fff',
  padding: theme.spacing(1, 2),
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.3)',
    background: '#8B5CF6',
  }
}));

export default function MeusSites() {
  const [sites, setSites] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [domainDialog, setDomainDialog] = useState({ open: false, site: null, loading: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const sitesPerPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    const carregarSites = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sites`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          const sortedSites = [...response.data.sites].sort((a, b) => new Date(b.dataCriacao || b.createdAt) - new Date(a.dataCriacao || a.createdAt));
          setSites(sortedSites);
        }
      } catch (err) {
        setSnackbar({ open: true, message: 'Erro ao carregar sites', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    carregarSites();
  }, []);

  const handleCopyUrl = (site) => {
    const url = site.url?.startsWith('http') ? site.url : `https://${site.url}`;
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'Link copiado!', severity: 'success' });
  };

  const handleOpenSite = (site) => {
    const url = site.url?.startsWith('http') ? site.url : `https://${site.url}`;
    window.open(url, '_blank');
  };

  const handleDelete = (site) => {
    setSelectedSite(site);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/sites/${selectedSite._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSites(sites.filter(s => s._id !== selectedSite._id));
      setSnackbar({ open: true, message: 'Site excluído com sucesso!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao excluir site', severity: 'error' });
    }
    setOpenDialog(false);
  };

  const handleRemoveCustomDomain = (site) => {
    setDomainDialog({ open: true, site, loading: false });
  };

  const confirmRemoveCustomDomain = async () => {
    if (!domainDialog.site) return;
    setDomainDialog(d => ({ ...d, loading: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/sites/${domainDialog.site._id}/custom-domain`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSites(prev => prev.map(s => s._id === domainDialog.site._id ? { ...s, customDomain: null } : s));
      setSnackbar({ open: true, message: 'Domínio próprio desativado com sucesso', severity: 'success' });
      setDomainDialog({ open: false, site: null, loading: false });
    } catch (err) {
      console.error('[MeusSites] erro ao desativar domínio próprio', { siteId: domainDialog.site?._id, status: err?.response?.status, msg: err?.message });
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao desativar domínio', severity: 'error' });
      setDomainDialog(d => ({ ...d, loading: false }));
    }
  };

  const filteredSites = sites.filter(site => {
    const termo = searchInput.toLowerCase();
    return (site.name || '').toLowerCase().includes(termo) || (site.url || '').toLowerCase().includes(termo);
  });

  const totalPages = Math.ceil(filteredSites.length / sitesPerPage);
  const paginatedSites = filteredSites.slice((page - 1) * sitesPerPage, page * sitesPerPage);

  const getStatus = (status) => {
    if (status === 'ativo' || status === 'active') return { label: 'Ativo', color: 'success', icon: <CheckCircle2 size={16} strokeWidth={1.75} style={{ color: '#10B981' }} /> };
    if (status === 'pending') return { label: 'Pendente', color: 'warning', icon: <Clock size={16} strokeWidth={1.75} style={{ color: '#F59E0B' }} /> };
    return { label: 'Erro', color: 'error', icon: <XCircle size={16} strokeWidth={1.75} style={{ color: '#EF4444' }} /> };
  };

  return (
    <Fade in timeout={400}>
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, color: '#fff' }}>
        Meus Sites
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <TextField
          size="small"
          placeholder="Pesquisar por domínio"
          value={searchInput}
          onChange={e => { setSearchInput(e.target.value); setPage(1); }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search size={20} strokeWidth={1.75} />
              </InputAdornment>
            )
          }}
          sx={{ width: '100%', maxWidth: 300 }}
        />
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : filteredSites.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', py: 8 }}>
          <SearchX size={80} strokeWidth={1.75} style={{ color: '#7C3AED', marginBottom: 16 }} />
          <Typography variant="h5" sx={{ color: '#9CA3AF', mb: 1, fontWeight: 600 }}>
            Nenhum site cadastrado ainda
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.disabled', mb: 3 }}>
            Crie seu primeiro site para vê-lo aqui!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/ftm-legacy')}
            sx={{ mt: 2, px: 4, py: 1.5, fontWeight: 600 }}
          >
            Criar Novo Site
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {paginatedSites.map((site) => {
            const status = getStatus(site.status);
            const url = site.url?.startsWith('http') ? site.url : `https://${site.url}`;
            return (
              <Grid item xs={12} sm={6} md={4} key={site._id}>
                <Paper elevation={4} sx={{
                  p: 3,
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  color: '#fff',
                  mb: 2,
                  minHeight: 220,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 24px 0 rgba(124,58,237,0.10)',
                  position: 'relative',
                  gap: 2
                }}>
                  <Box sx={{ position: 'absolute', top: 18, right: 18, zIndex: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/configuracoes-site/${site._id}`)}
                      sx={{
                        color: '#7C3AED',
                        background: 'rgba(124,58,237,0.08)',
                        boxShadow: '0 2px 8px 0 rgba(124,58,237,0.10)',
                        '&:hover': { background: 'rgba(124,58,237,0.18)' }
                      }}
                    >
                      <Pencil size={20} strokeWidth={1.75} />
                    </IconButton>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 90 }}>
                    <Typography variant="h6" sx={{ color: '#7C3AED', fontWeight: 700, mb: 1, wordBreak: 'break-all', pr: 4 }}>
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                        <ExternalLink size={16} strokeWidth={1.75} style={{ marginRight: 8, cursor: 'pointer' }} onClick={() => handleOpenSite(site)} />
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#7C3AED', textDecoration: 'underline', fontWeight: 700 }}
                        >
                          {url.replace(/^https?:\/\//, '')}
                        </a>
                      </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 1 }}>
                      Criado em: {new Date(site.createdAt).toLocaleDateString('pt-BR')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mt: 2, gap: 1 }}>
                    <Chip icon={status.icon} label={status.label} color={status.color} size="small" sx={{ fontWeight: 600 }} />
                    <IconButton size="small" onClick={() => handleCopyUrl(site)} sx={{ color: '#7C3AED' }}>
                      <Copy size={20} strokeWidth={1.75} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenSite(site)} sx={{ color: '#10B981' }}>
                      <ExternalLink size={20} strokeWidth={1.75} />
                    </IconButton>
                    {site.customDomain && (
                      <Tooltip title={`Desativar domínio próprio (${site.customDomain})`}>
                        <IconButton size="small" onClick={() => handleRemoveCustomDomain(site)} sx={{ color: '#F59E0B' }}>
                          <Link2Off size={20} strokeWidth={1.75} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" onClick={() => handleDelete(site)} sx={{ color: '#EF4444', ml: 'auto' }}>
                      <Trash2 size={20} strokeWidth={1.75} />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
      {!loading && filteredSites.length > 0 && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} shape="rounded" />
        </Box>
      )}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            background: '#141926',
            border: '1px solid rgba(255,255,255,0.06)',
            color: '#fff'
          }
        }}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#9CA3AF' }}>
            Tem certeza que deseja excluir o site "{selectedSite?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{ color: '#9CA3AF' }}
          >
            Cancelar
          </Button>
          <StyledButton onClick={confirmDelete}>
            Excluir
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={domainDialog.open}
        onClose={() => { if (!domainDialog.loading) setDomainDialog({ open: false, site: null, loading: false }); }}
        PaperProps={{
          sx: {
            background: '#141926',
            border: '1px solid rgba(255,255,255,0.06)',
            color: '#fff'
          }
        }}
      >
        <DialogTitle>Desativar domínio próprio?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#9CA3AF', mb: 2 }}>
            O site deixará de responder pelo domínio <strong style={{ color: '#F9FAFB' }}>{domainDialog.site?.customDomain}</strong>. A configuração do nginx para esse domínio será removida e o servidor parará de reconhecê-lo.
          </Typography>
          <Typography sx={{ color: '#9CA3AF', mb: 2 }}>
            Se você tinha email configurado nesse domínio, ele também deixará de funcionar.
          </Typography>
          <Typography sx={{ color: '#9CA3AF', mb: 2 }}>
            Para usar o domínio novamente, você precisará refazer o processo no wizard de criação.
          </Typography>
          <Typography sx={{ color: '#F59E0B', fontWeight: 600 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDomainDialog({ open: false, site: null, loading: false })}
            disabled={domainDialog.loading}
            sx={{ color: '#9CA3AF' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmRemoveCustomDomain}
            disabled={domainDialog.loading}
            variant="contained"
            sx={{ background: '#F59E0B', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#D97706' } }}
          >
            {domainDialog.loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Desativar domínio'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            background: '#141926',
            border: '1px solid rgba(255,255,255,0.06)',
            color: '#fff'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Fade>
  );
}
